import { forwardRef } from "react";

import { Tabs } from "antd";

import { cn } from "@btnet/utilities-core";

import "./BuiTabs.less";
import { type IBuiTabPaneProps, type IBuiTabsProps } from "./BuiTabs.types";

/**
 * BuiTabPane is a minimal wrapper around antd Tabs.TabPane.
 * Represents an individual tab panel within BuiTabs.
 * Accepts any ReactNode for maximum composability.
 * @param props - The tab pane properties
 * @param props.children - The content to render inside the tab pane
 * @example
 * ```tsx
 * <BuiTabs.TabPane
 *   tab={<><BdsIcon icon="home" /> Home</>}
 *   key="home"
 *   disabled={false}
 * >
 *   Home content
 * </BuiTabs.TabPane>
 * ```
 */
const BuiTabPane = function BuiTabPane({
  children,
  ...props
}: IBuiTabPaneProps) {
  return <Tabs.TabPane {...props}>{children}</Tabs.TabPane>;
};

/**
 * BuiTabs is a minimal wrapper around antd Tabs (4.16.12) with BDS patterns.
 * Provides composable tab navigation with support for:
 * - Controlled and uncontrolled modes
 * - Multiple tab positions (top, bottom, left, right)
 * - Icons and custom content in tabs
 * - Disabled tabs
 * - Editable tabs (add/remove)
 *
 * For WAI-ARIA keyboard navigation, use the `useTabsKeyboardNavigation` hook
 * with a ref passed to this component.
 * @param props - The tabs component properties
 * @param props.testid - Test ID for testing purposes
 * @param props.id - Unique identifier for the tabs component
 * @param props.className - Additional CSS classes to apply
 * @param props.children - Tab pane children
 * @param props.tabPosition - Position of the tabs (top, bottom, left, right)
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * useTabsKeyboardNavigation({ containerRef: ref, tabPosition: "top" });
 *
 * <BuiTabs ref={ref} testid="my-tabs" defaultActiveKey="1">
 *   <BuiTabs.TabPane tab="Tab 1" key="1">
 *     Content 1
 *   </BuiTabs.TabPane>
 *   <BuiTabs.TabPane tab="Tab 2" key="2">
 *     Content 2
 *   </BuiTabs.TabPane>
 * </BuiTabs>
 * ```
 */
const BuiTabsComponent = forwardRef<HTMLDivElement, IBuiTabsProps>(
  ({ testid, id, className, children, tabPosition = "top", ...props }, ref) => {
    return (
      <div
        ref={ref}
        id={id}
        data-testid={testid}
        className={cn("bui-tabs", className)}
      >
        <Tabs {...props} tabPosition={tabPosition}>
          {children}
        </Tabs>
      </div>
    );
  },
);

BuiTabsComponent.displayName = "BuiTabs";

type BuiTabsType = typeof BuiTabsComponent & {
  TabPane: typeof BuiTabPane;
};

export const BuiTabs = BuiTabsComponent as BuiTabsType;
BuiTabs.TabPane = BuiTabPane;
